import { useEffect, useState, useRef, useCallback } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import InspectionCard from './InspectionCard';
import { Postrequestcall } from '../../request/Postrequest';
import { useSelector } from 'react-redux';
import Loader from '../../common/Loader';
import { Box, Fab, Typography } from '@mui/material';
import { Add } from '@mui/icons-material';
import NewInspectionModal from './NewInspectionModal';
import { FETCH_CLIENT_INSPECTION_FORM_ANSWERS_2 } from '../../constant/Apipath';
import { ClientEnglishFallback } from '../constant/ClientEnglishFallback';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';

export const Inspections = () => {
    const [inspectionFormTemplateList, setInspectionFormTemplateList] = useState([]);
    const [isInspectionLoading, setIsInspectionLoading] = useState(false);
    const [openNewInspectionModal, setOpenNewInspectionModal] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const pageSize = 20;
    const totalCountRef = useRef(null);
    const { loginData, selectedClientData, multiLanguageData } = useSelector((state) => state?.main);

    const accentColor = selectedClientData?.AccentColor || '#4F6EF7';

    const LANGUAGE = ClientEnglishFallback.ClientInspections(
        multiLanguageData?.ClientInspections || {}
    );
    const { TEXT_NO_INSPECTION_FORM_AVAILABLE } = LANGUAGE;

    const removeDuplicates = (array, prop) =>
        Array.from(new Map(array.map((item) => [item[prop], item])).values());

    const getList = useCallback(
        async (pageNr = 1, append = false) => {
            if (pageNr === 1) setIsInspectionLoading(true);
            try {
                const requestPayload = {
                    PageNr: pageNr,
                    NrOfRecPerPage: pageSize,
                    FullSearch: '',
                    UserId: loginData?.UserInformation?.UserId,
                    IncludeRecordNr: true,
                    FetchAllowedRecordsOnly: false,
                    SortList: [{ FieldName: 'DateModified', Direction: 'DESC' }],
                    SearchList: [{ employeeId: selectedClientData?.ClientEmployeeId }],
                };
                const res = await Postrequestcall(
                    FETCH_CLIENT_INSPECTION_FORM_ANSWERS_2,
                    requestPayload,
                    loginData?.accessToken
                );
                if (
                    res?.data?.data &&
                    res?.data?.status === true &&
                    res?.data?.message === 'OK'
                ) {
                    const dataArr = res?.data?.data;
                    if (append) {
                        setInspectionFormTemplateList((prev) =>
                            removeDuplicates([...prev, ...dataArr], 'id')
                        );
                    } else {
                        setInspectionFormTemplateList(dataArr);
                    }
                    if (typeof res?.data?.totalCount === 'number') {
                        totalCountRef.current = res.data.totalCount;
                        setHasMore(pageNr * pageSize < res.data.totalCount);
                    } else {
                        setHasMore(dataArr.length === pageSize);
                    }
                } else {
                    setHasMore(false);
                }
            } catch (error) {
                setHasMore(false);
                console.log('Error : ', error);
            } finally {
                if (pageNr === 1) setIsInspectionLoading(false);
            }
        },
        [loginData, selectedClientData]
    );

    useEffect(() => {
        setPage(1);
        getList(1, false);
    }, [getList]);

    const fetchMoreData = () => {
        const nextPage = page + 1;
        setPage(nextPage);
        getList(nextPage, true);
    };

    return (
        <>
            {isInspectionLoading && page === 1 ? (
                <Loader fullScreen />
            ) : inspectionFormTemplateList?.length > 0 ? (
                <Box
                    id="inspection-list-scrollable-div"
                    sx={{
                        maxHeight: 'calc(100vh - 80px)',
                        overflow: 'auto',
                        px: 1.5,
                        pt: 1,
                        pb: 10,
                    }}
                >
                    <InfiniteScroll
                        dataLength={inspectionFormTemplateList.length}
                        next={fetchMoreData}
                        hasMore={hasMore}
                        loader={
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                <Loader />
                            </Box>
                        }
                        scrollableTarget="inspection-list-scrollable-div"
                        style={{ overflow: 'visible' }}
                    >
                        {inspectionFormTemplateList.map((inspectionForm) => (
                            <InspectionCard
                                key={inspectionForm.id}
                                inspectionForm={inspectionForm}
                                getList={getList}
                            />
                        ))}
                    </InfiniteScroll>
                </Box>
            ) : (
                /* ── Empty State ── */
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 'calc(100vh - 80px)',
                        gap: 2,
                        px: 3,
                    }}
                >
                    <Box
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: '20px',
                            bgcolor: `${accentColor}14`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mb: 1,
                        }}
                    >
                        <AssignmentOutlinedIcon sx={{ fontSize: 36, color: accentColor }} />
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: "#1A1D23",  fontSize: '1rem', letterSpacing: '-0.01em' }} variant="h6" textAlign="center">
                        {TEXT_NO_INSPECTION_FORM_AVAILABLE}
                    </Typography>
                    <Typography
                        variant="body2"
                        
                        textAlign="center"
                        sx={{color: "#9499A8",  maxWidth: 260, lineHeight: 1.6 }}
                    >
                        Tap the + button below to start a new inspection form.
                    </Typography>
                </Box>
            )}

            {/* FAB */}
            <Fab
                aria-label="add"
                sx={{
                    position: 'fixed',
                    bottom: 28,
                    right: 24,
                    color: 'white',
                    background: `linear-gradient(135deg, ${accentColor}, ${accentColor}CC)`,
                    boxShadow: `0 6px 20px ${accentColor}55`,
                    '&:hover': {
                        background: `linear-gradient(135deg, ${accentColor}EE, ${accentColor}AA)`,
                        boxShadow: `0 8px 24px ${accentColor}66`,
                    },
                    transition: 'all 0.2s ease',
                }}
                onClick={() => setOpenNewInspectionModal(true)}
            >
                <Add />
            </Fab>

            {openNewInspectionModal && (
                <NewInspectionModal
                    open={openNewInspectionModal}
                    onClose={() => setOpenNewInspectionModal(false)}
                    getList={getList}
                />
            )}
        </>
    );
};